import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { useRTL } from '../shared/hooks/useRTL';
import appVersionService from '../services/appVersionService';
import { ShimmerTable } from '../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const Settings = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const { getRTLClasses, getFlexDirection } = useRTL();
  const [activeTab, setActiveTab] = useState('app-versions');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // App Versions State
  const [versions, setVersions] = useState([]);
  const [showVersionForm, setShowVersionForm] = useState(false);
  const [editingVersionId, setEditingVersionId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [versionForm, setVersionForm] = useState({
    force_update_version: '',
    minimum_version: '',
    message: '',
    store_urls: {
      ios: '',
      android: ''
    },
    is_active: true
  });

  useEffect(() => {
    if (token) {
      appVersionService.setAuthToken(token);
      if (activeTab === 'app-versions') {
        fetchVersions();
      }
    }
  }, [token, activeTab]);

  const fetchVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await appVersionService.getAllVersions({ limit: 100, offset: 0 });
      setVersions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('store_urls.')) {
      const storeType = name.split('.')[1];
      setVersionForm(prev => ({
        ...prev,
        store_urls: {
          ...prev.store_urls,
          [storeType]: value
        }
      }));
    } else {
      setVersionForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleCreateVersion = () => {
    setVersionForm({
      force_update_version: '',
      minimum_version: '',
      message: '',
      store_urls: {
        ios: '',
        android: ''
      },
      is_active: true
    });
    setEditingVersionId(null);
    setShowVersionForm(true);
  };

  const handleEditVersion = (version) => {
    setVersionForm({
      force_update_version: version.force_update_version || '',
      minimum_version: version.minimum_version || '',
      message: version.message || '',
      store_urls: {
        ios: version.store_urls?.ios || '',
        android: version.store_urls?.android || ''
      },
      is_active: version.is_active !== undefined ? version.is_active : true
    });
    setEditingVersionId(version.id);
    setShowVersionForm(true);
  };

  const handleDeleteVersion = async (id) => {
    if (!window.confirm(t('areYouSureDeleteVersion'))) return;
    setActionLoading(true);
    try {
      await appVersionService.deleteVersion(id);
      setVersions(prev => prev.filter(v => v.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleVersionSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError(null);
    try {
      if (editingVersionId) {
        const updated = await appVersionService.updateVersion(editingVersionId, versionForm);
        setVersions(prev => prev.map(v => v.id === editingVersionId ? updated : v));
      } else {
        const created = await appVersionService.createVersion(versionForm);
        setVersions(prev => [created, ...prev]);
      }
      setShowVersionForm(false);
      setVersionForm({
        force_update_version: '',
        minimum_version: '',
        message: '',
        store_urls: {
          ios: '',
          android: ''
        },
        is_active: true
      });
      setEditingVersionId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className={`${getRTLClasses('')} max-w-full overflow-x-hidden space-y-6`}>
      <PageHeader
        title={t('settings')}
        description={t('manageSystemSettings')}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="app-versions">{t('appVersions')}</TabsTrigger>
        </TabsList>

        <TabsContent value="app-versions" className="space-y-4">
          <div className={`flex items-center justify-between gap-4 ${getFlexDirection()}`}>
            <h2 className="text-xl font-semibold text-foreground">
              {t('appVersionManagement')}
            </h2>
            <Button onClick={handleCreateVersion}>
              {t('addAppVersion')}
            </Button>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {loading ? (
            <div className="space-y-4">
              <ShimmerTable rows={5} columns={6} />
            </div>
          ) : (
            <>
              <Card className="hidden lg:block">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('forceUpdateVersion')}</TableHead>
                        <TableHead>{t('minimumVersion')}</TableHead>
                        <TableHead>{t('message')}</TableHead>
                        <TableHead>{t('status')}</TableHead>
                        <TableHead>{t('createdAt')}</TableHead>
                        <TableHead>{t('actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {versions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                            {t('noAppVersionsFound')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        versions.map(version => (
                          <TableRow key={version.id}>
                            <TableCell>{version.force_update_version || '-'}</TableCell>
                            <TableCell>{version.minimum_version || '-'}</TableCell>
                            <TableCell className="max-w-xs truncate">
                              {version.message || '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={version.is_active ? 'success' : 'secondary'}>
                                {version.is_active ? t('active') : t('inactive')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {version.created_at ? new Date(version.created_at).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                              <div className={`flex gap-2 ${getFlexDirection()}`}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditVersion(version)}
                                  disabled={actionLoading}
                                >
                                  {t('edit')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteVersion(version.id)}
                                  disabled={actionLoading}
                                >
                                  {t('delete')}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <div className="space-y-4 lg:hidden">
                {versions.length === 0 ? (
                  <p className="py-8 text-center text-muted-foreground">
                    {t('noAppVersionsFound')}
                  </p>
                ) : (
                  versions.map(version => (
                    <Card key={version.id}>
                      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                        <div className="flex-1 space-y-1">
                          <CardTitle className="text-lg">
                            {version.force_update_version || t('noVersion')}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground">
                            {t('minimumVersion')}: {version.minimum_version || '-'}
                          </p>
                        </div>
                        <Badge variant={version.is_active ? 'success' : 'secondary'}>
                          {version.is_active ? t('active') : t('inactive')}
                        </Badge>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {version.message && (
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">{t('message')}:</span>{' '}
                            {version.message}
                          </p>
                        )}

                        <div className="space-y-2 text-sm">
                          {version.store_urls?.ios && (
                            <div className="flex justify-between gap-2">
                              <span className="text-muted-foreground">{t('iosStoreUrl')}:</span>
                              <a
                                href={version.store_urls.ios}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="max-w-xs truncate text-primary hover:underline"
                              >
                                {t('view')}
                              </a>
                            </div>
                          )}
                          {version.store_urls?.android && (
                            <div className="flex justify-between gap-2">
                              <span className="text-muted-foreground">{t('androidStoreUrl')}:</span>
                              <a
                                href={version.store_urls.android}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="max-w-xs truncate text-primary hover:underline"
                              >
                                {t('view')}
                              </a>
                            </div>
                          )}
                          <div className="flex justify-between gap-2">
                            <span className="text-muted-foreground">{t('createdAt')}:</span>
                            <span>
                              {version.created_at ? new Date(version.created_at).toLocaleDateString() : '-'}
                            </span>
                          </div>
                        </div>

                        <div className={`flex gap-2 ${getFlexDirection()}`}>
                          <Button
                            className="flex-1"
                            variant="outline"
                            onClick={() => handleEditVersion(version)}
                            disabled={actionLoading}
                          >
                            {t('edit')}
                          </Button>
                          <Button
                            className="flex-1"
                            variant="destructive"
                            onClick={() => handleDeleteVersion(version.id)}
                            disabled={actionLoading}
                          >
                            {t('delete')}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </>
          )}

          <Dialog
            open={showVersionForm}
            onOpenChange={(open) => {
              setShowVersionForm(open);
              if (!open) {
                setEditingVersionId(null);
                setError(null);
              }
            }}
          >
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingVersionId ? t('editAppVersion') : t('addAppVersion')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleVersionSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="force_update_version">
                      {t('forceUpdateVersion')} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="force_update_version"
                      type="text"
                      name="force_update_version"
                      value={versionForm.force_update_version}
                      onChange={handleVersionInputChange}
                      placeholder="1.0.0"
                      pattern="^\d+\.\d+\.\d+"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('versionFormatHint')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="minimum_version">
                      {t('minimumVersion')} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="minimum_version"
                      type="text"
                      name="minimum_version"
                      value={versionForm.minimum_version}
                      onChange={handleVersionInputChange}
                      placeholder="1.0.0"
                      pattern="^\d+\.\d+\.\d+"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('versionFormatHint')}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t('message')}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={versionForm.message}
                    onChange={handleVersionInputChange}
                    rows={3}
                    placeholder={t('updateMessagePlaceholder')}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="store_urls_ios">{t('iosStoreUrl')}</Label>
                    <Input
                      id="store_urls_ios"
                      type="url"
                      name="store_urls.ios"
                      value={versionForm.store_urls.ios}
                      onChange={handleVersionInputChange}
                      placeholder="https://apps.apple.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="store_urls_android">{t('androidStoreUrl')}</Label>
                    <Input
                      id="store_urls_android"
                      type="url"
                      name="store_urls.android"
                      value={versionForm.store_urls.android}
                      onChange={handleVersionInputChange}
                      placeholder="https://play.google.com/..."
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={!!versionForm.is_active}
                    onCheckedChange={(checked) =>
                      setVersionForm(prev => ({ ...prev, is_active: checked }))
                    }
                  />
                  <Label htmlFor="is_active">{t('active')}</Label>
                </div>

                <DialogFooter className={getFlexDirection()}>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowVersionForm(false);
                      setEditingVersionId(null);
                      setError(null);
                    }}
                    disabled={actionLoading}
                  >
                    {t('cancel')}
                  </Button>
                  <Button type="submit" disabled={actionLoading}>
                    {actionLoading ? t('saving') : (editingVersionId ? t('update') : t('create'))}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
